"use client";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Icons } from "@/components/Icons";

type StarRatingProps = {
  value: number;
  onChange?: (n: number) => void;
  size?: number;
  /** Presentational label under the stars (e.g. "Not great" / "Loved it"). */
  helperText?: React.ReactNode;
  readOnly?: boolean;
  /** Row justification — "center" (default, primary rating prompt) or "start" (compact rows, e.g. ItemRatingRow). */
  justify?: "center" | "flex-start";
  /** Override the group's aria-label (defaults to the generic 1-5 prompt). Useful for per-item rows. */
  groupLabel?: string;
};

/**
 * Accessible, RTL-aware 1-5 star rating input.
 *
 * - Renders as a `radiogroup` of five `radio` buttons (keyboard + SR friendly).
 * - Fill color comes from `--primary-600` (mock's brand-green stars, not gold).
 * - RTL mirroring: the row is laid out with CSS logical properties
 *   (`flexDirection: row` + `dir` inherited from the document), so in RTL
 *   documents the 1st star renders on the visual right and taps still map
 *   1..5 in reading order — no manual index reversal needed, and no
 *   visual "star 5 lights up first" bug.
 */
export function StarRating({
  value,
  onChange,
  size = 46,
  helperText,
  readOnly = false,
  justify = "center",
  groupLabel,
}: StarRatingProps) {
  const { t } = useTranslation();
  const stars = [1, 2, 3, 4, 5];

  return (
    <div>
      <div
        role="radiogroup"
        aria-label={groupLabel ?? (t("v2.reviews.rate_aria", "Rate 1 to 5 stars") as string)}
        style={{
          display: "flex",
          justifyContent: justify,
          gap: size >= 40 ? 13 : size >= 30 ? 10 : 3,
          marginBottom: helperText != null ? 12 : 0,
        }}
      >
        {stars.map((n) => {
          const filled = n <= value;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={value === n}
              aria-label={t("v2.reviews.star_label", "{{n}} star", { n }) as string}
              disabled={readOnly}
              onClick={() => !readOnly && onChange?.(n)}
              style={{
                background: "transparent",
                border: 0,
                padding: 4,
                cursor: readOnly ? "default" : "pointer",
                display: "grid",
                placeItems: "center",
                color: filled ? "var(--primary-600)" : "var(--surface-2-strong, #e2e6e1)",
              }}
            >
              <Icons.star size={size} />
            </button>
          );
        })}
      </div>
      {helperText != null && (
        <div style={{ textAlign: "center", fontSize: 13, fontWeight: 500, color: "var(--ink-3)" }}>
          {helperText}
        </div>
      )}
    </div>
  );
}
